import type { CaptionStyle } from '../../shared/captionStyle';
import type { CaptionAt, SnapSticker, UploadTarget } from '../../shared/types';
import { DAILY_NAME } from '../../shared/constants';
import { announceDaily, setDailyDecor } from './data';
import {
  sendLeg,
  uploadFile,
  uploadPhoto,
  uploadVideo,
  uploadVoice,
  type BeforeSend,
  type UploadHandle,
} from './media/upload';
import { allJobs, forgetJob, forgetLeg, hasJob, lasting, legOf, saveJob, saveLeg } from './uploadDb';
import { LEASE_MS, LEG_SYNC, RENEW_MS } from './uploadLegs';
import { auth } from './firebase';

/**
 * A background upload queue that survives the app closing.
 *
 * ── What is and is not possible ─────────────────────────────────────────────
 * An upload cannot *continue* while the app is closed on iOS. Background Sync
 * and Background Fetch, the two APIs built for exactly this, are Chrome and
 * Android only; Safari implements neither, and once a PWA is closed none of
 * our JavaScript runs. Anyone claiming otherwise on iOS is mistaken.
 *
 * So instead of pretending, jobs are persisted to IndexedDB with their bytes
 * and resumed on the next launch. From the family's point of view nothing is
 * lost and nothing has to be re-picked: they reopen Givi Den and the photo
 * finishes going up. Combined with reconcileUploads on the server, which
 * adopts anything whose bytes reached R2 but whose document was never
 * written, no upload can fall through the gap.
 *
 * Jobs are data rather than closures precisely so they can be written to
 * IndexedDB and rebuilt later. Resuming re-runs the whole flow — a fresh
 * createUpload, a fresh presigned PUT — because a presigned URL only lives
 * fifteen minutes and would be stale by the time anyone reopens the app. The
 * abandoned first attempt leaves an uploads/{id} document that the sweep
 * marks abandoned, which is what that state is for.
 */

export type QueuedUpload =
  | { id: string; kind: 'chat-photo'; file: File; target: UploadTarget }
  | { id: string; kind: 'chat-file'; file: File; target: UploadTarget }
  | { id: string; kind: 'chat-video'; file: File; target: UploadTarget }
  | {
      id: string;
      kind: 'chat-voice';
      file: File;
      durationSec: number;
      target: UploadTarget;
    }
  | {
      id: string;
      kind: 'daily';
      day: string;
      postId: string;
      back: File;
      front: File;
      caption?: string;
      captionAt?: CaptionAt;
      captionStyle?: CaptionStyle;
      stickers?: SnapSticker[];
    }
  | { id: string; kind: 'daily-video'; day: string; postId: string; file: File }
  | {
      id: string;
      kind: 'extra';
      day: string;
      postId: string;
      file: File;
      video: boolean;
      caption?: string;
      captionAt?: CaptionAt;
    }
  | { id: string; kind: 'avatar'; uid: string; file: File };

/** Omit across a union, rather than collapsing it to the shared keys. */
type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never;
export type NewUpload = WithoutId<QueuedUpload>;

export interface QueueState {
  /** What is going up, and the photo itself when it is one, to show small beside it. */
  active: { label: string; progress: number; picture?: Blob } | null;
  queued: number;
  error: string | null;
  /** Restored from a previous session and waiting to run. */
  resumed: number;
}

let state: QueueState = { active: null, queued: 0, error: null, resumed: 0 };
const listeners = new Set<(next: QueueState) => void>();
const pending: QueuedUpload[] = [];
/** Shifted off `pending` and currently uploading. */
const inFlight = new Set<string>();
let running = false;

/*
 * Cut off by the app going to the background, which kills the request while
 * the page lives on. That is not something having gone wrong: the upload is
 * held here and simply carries on when the app is back, without telling
 * anyone it "did not finish". Kept in memory as well as IndexedDB, so it
 * carries on even where IndexedDB is refused.
 */
const stalled: QueuedUpload[] = [];
/** When the app last went to the background. */
let awayAt = 0;
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') awayAt = Date.now();
  });
}

/** The photo a job is sending, if it is sending one. A clip, a voice note or a file has nothing to show. */
export function pictureOfJob(job: QueuedUpload): Blob | undefined {
  switch (job.kind) {
    case 'chat-photo':
    case 'avatar':
      return job.file;
    case 'daily':
      return job.back;
    case 'extra':
      return job.video ? undefined : job.file;
    default:
      return undefined;
  }
}

function label(job: QueuedUpload): string {
  if (job.kind === 'extra') return job.video ? 'your clip' : 'your photo';
  if (job.kind === 'avatar') return 'your picture';
  if (job.kind === 'daily' || job.kind === 'daily-video') return `your ${DAILY_NAME}`;
  if (job.kind === 'chat-video') return 'a video';
  if (job.kind === 'chat-voice') return 'a voice note';
  if (job.kind === 'chat-file') return job.file.name || 'a file';
  // A snap travels as a chat photo with a snap target; it is still a snap.
  if (job.kind === 'chat-photo' && job.target.kind === 'snap') return 'a snap';
  return 'a photo';
}

function publish(next: Partial<QueueState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener(state);
}

export function subscribeToUploads(listener: (next: QueueState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

export function dismissUploadError(): void {
  publish({ error: null });
}

// --- persistence ------------------------------------------------------------
// In ./uploadDb, which the service worker opens too: see ./uploadLegs.

// --- queue ------------------------------------------------------------------

/*
 * Whether each job this page queued made it into IndexedDB. A big clip can be
 * refused for room, and a job missing from the store otherwise reads as one
 * the service worker finished: drain skipped it and the snap was gone without
 * a word. One that never got there is held in memory instead, and sent from
 * here. Jobs restored from the store have no entry, and are there by
 * definition.
 */
const saved = new Map<string, Promise<boolean>>();

export function enqueueUpload(job: NewUpload): void {
  const full = { ...job, id: crypto.randomUUID() } as QueuedUpload;
  pending.push(full);
  publish({ queued: pending.length, error: null });
  const stored = saveJob(full).then(
    () => true,
    () => false,
  );
  saved.set(full.id, stored);
  void stored.then(() => drain());
}

/**
 * Turned away by createUpload for what it is -- too long, not allowed -- which
 * no retry can change. Kept, it would go round and fail again on every return.
 */
export function refusedForGood(err: unknown): boolean {
  const code = (err as { code?: string } | null)?.code;
  return code === 'functions/invalid-argument' || code === 'functions/permission-denied';
}

/**
 * Each job once, and none already queued or uploading. An upload cut off by
 * leaving is held in memory and saved in IndexedDB both, and sending both
 * copies would post the same snap twice.
 */
export function newJobsOf(jobs: QueuedUpload[], known: Iterable<string>): QueuedUpload[] {
  const seen = new Set(known);
  return jobs.filter((job) => {
    if (seen.has(job.id)) return false;
    seen.add(job.id);
    return true;
  });
}

/**
 * Called once at startup. Anything left from a previous session goes back on
 * the queue and runs immediately.
 */
export async function resumeUploads(): Promise<number> {
  const held = stalled.splice(0);
  const saved = [...held, ...(await allJobs<QueuedUpload>())];
  if (saved.length === 0) return 0;

  // Skip anything already queued or mid-flight. Without the in-flight set, a
  // resume triggered while an upload is running would start it a second time.
  // Held in memory and saved to IndexedDB is the same job, so one of each.
  const restored = newJobsOf(saved, [...pending.map((job) => job.id), ...inFlight]);
  if (restored.length === 0) return 0;
  pending.push(...restored);

  // Trying again is the answer to whatever went wrong last time, so last
  // time's complaint goes with it.
  publish({ queued: pending.length, resumed: restored.length, error: null });
  void drain();
  return restored.length;
}

// --- handing on to the service worker ----------------------------------------

/*
 * A job hands its last leg on once its ticket is in hand, so the worker can
 * finish it if the app closes: see ./uploadLegs. runOnPage passes the hand-on
 * to every upload but a GiviSnap's, which has more to do once it lands -- its
 * words, its pointer in chat -- that only the page can.
 */

/** The page's renewing hold on each leg it is sending. */
const holds = new Map<string, ReturnType<typeof setInterval>>();

function hold(jobId: string): void {
  release(jobId);
  holds.set(
    jobId,
    setInterval(() => {
      void legOf(jobId).then((leg) => {
        // Let go of meanwhile: renewing now would bring back a finished leg.
        if (leg && holds.has(jobId))
          void saveLeg({ ...leg, leaseUntil: Date.now() + LEASE_MS }).catch(() => undefined);
      });
    }, RENEW_MS),
  );
}

function release(jobId: string): void {
  clearInterval(holds.get(jobId));
  holds.delete(jobId);
}

/** Wakes the worker when the phone is online, even with the app closed; Chrome only. */
async function askForSync(): Promise<void> {
  try {
    const registration = (await navigator.serviceWorker?.ready) as
      | (ServiceWorkerRegistration & { sync?: { register: (tag: string) => Promise<void> } })
      | undefined;
    await registration?.sync?.register(LEG_SYNC);
  } catch {
    // No background sync: the leg waits for the page, as before.
  }
}

function handOff(job: QueuedUpload): BeforeSend {
  return async (draft) => {
    const idToken = (await auth.currentUser?.getIdToken().catch(() => null)) ?? '';
    // Not stored, the worker cannot take it over; the page still sends it.
    const handed = await saveLeg({ ...draft, jobId: job.id, idToken, leaseUntil: Date.now() + LEASE_MS }).then(
      () => true,
      () => false,
    );
    if (!handed) return;
    hold(job.id);
    void askForSync();
  };
}

async function run(job: QueuedUpload, onProgress: (f: number) => void): Promise<void> {
  /*
   * Handed on before and not finished: the same ticket, picked up again, not
   * a new one -- that would be a second copy of the same photo. A ticket too
   * close to running out starts over.
   */
  const saved = await legOf(job.id);
  try {
    if (saved && saved.expiresAt - Date.now() > 60_000) {
      await saveLeg({ ...saved, leaseUntil: Date.now() + LEASE_MS });
      hold(job.id);
      await sendLeg(saved, onProgress).promise;
      return;
    }
    if (saved) await forgetLeg(job.id);
    await runOnPage(job, onProgress, handOff(job));
  } finally {
    release(job.id);
  }
}

async function runOnPage(
  job: QueuedUpload,
  onProgress: (f: number) => void,
  beforeSend?: BeforeSend,
): Promise<void> {
  if (job.kind === 'avatar') {
    // The user doc is written by finalizeUpload, so there is nothing to do
    // here afterwards: the new face arrives through the same listener that
    // was already watching everyone.
    await uploadPhoto(job.file, { kind: 'avatar', id: job.uid }, onProgress, 'photo', beforeSend)
      .promise;
    return;
  }

  // A GiviSnap, from here to the chat upload below, never hands its leg on:
  // what it has to do once it lands -- its words, its pointer in chat -- only
  // the page can do.
  if (job.kind === 'extra') {
    const target = { kind: 'daily' as const, id: job.day, postId: job.postId, extra: true };
    await (job.video
      ? uploadVideo(job.file, target, onProgress, 'video')
      : uploadPhoto(job.file, target, onProgress, 'photo')
    ).promise;

    /*
     * The caption, once the post exists to carry it. A clip's post is written
     * by the transcoder rather than by the upload, so this can arrive before
     * there is anything to write to — which is why it is allowed to fail
     * quietly and why a clip keeps its caption for the retry rather than
     * losing it here.
     */
    if (job.caption) {
      await setDailyDecor(job.day, job.postId, { caption: job.caption, captionAt: job.captionAt }).catch(
        () => undefined,
      );
    }
    // No pointer in chat: an extra is not an answer to the prompt.
    return;
  }

  if (job.kind === 'daily-video') {
    await uploadVideo(
      job.file,
      { kind: 'daily', id: job.day, postId: job.postId },
      onProgress,
      'video',
    ).promise;
    await announceDaily({ day: job.day, postId: job.postId });
    return;
  }

  if (job.kind === 'daily') {
    // Paired, small and independent, so together rather than in sequence.
    const progress = { back: 0, front: 0 };
    const report = () => onProgress((progress.back + progress.front) / 2);

    await Promise.all([
      uploadPhoto(
        job.back,
        { kind: 'daily', id: job.day, postId: job.postId },
        (p) => {
          progress.back = p;
          report();
        },
        'back',
      ).promise,
      uploadPhoto(
        job.front,
        { kind: 'daily', id: job.day, postId: job.postId },
        (p) => {
          progress.front = p;
          report();
        },
        'front',
      ).promise,
    ]);

    // Both have landed, so the post document exists and will take its words
    // and emojis.
    if (job.caption || job.stickers?.length) {
      await setDailyDecor(job.day, job.postId, {
        caption: job.caption,
        captionAt: job.captionAt,
        captionStyle: job.captionStyle,
        stickers: job.stickers,
      }).catch(() => undefined);
    }

    // Only once both have landed, or chat points at a half-written daily.
    await announceDaily({ day: job.day, postId: job.postId });
    return;
  }

  const handle: UploadHandle =
    job.kind === 'chat-video'
      ? uploadVideo(job.file, job.target, onProgress, undefined, beforeSend)
      : job.kind === 'chat-voice'
        ? uploadVoice(job.file, job.durationSec, job.target, onProgress, beforeSend)
        : job.kind === 'chat-file'
          ? uploadFile(job.file, job.target, onProgress, beforeSend)
          : uploadPhoto(job.file, job.target, onProgress, undefined, beforeSend);
  await handle.promise;
}

async function drain(): Promise<void> {
  if (running) return;
  running = true;

  while (pending.length > 0) {
    const job = pending.shift();
    if (!job) break;
    // In flight before anything is awaited, or a resume in the gap would
    // queue the same job a second time.
    inFlight.add(job.id);
    // Finished by the service worker while the app was away -- unless it was
    // never stored, in which case missing says nothing about it.
    const stored = (await saved.get(job.id)) ?? true;
    if (stored && (await lasting()) && !(await hasJob(job.id))) {
      saved.delete(job.id);
      inFlight.delete(job.id);
      continue;
    }
    publish({
      active: { label: label(job), progress: 0, picture: pictureOfJob(job) },
      queued: pending.length,
      resumed: 0,
    });

    const startedAt = Date.now();
    try {
      await run(job, (fraction) =>
        publish({ active: { label: label(job), progress: Math.min(1, fraction), picture: pictureOfJob(job) } }),
      );
      // It went. A record that will not clear is no reason to say it did not.
      await forgetLeg(job.id).catch(() => undefined);
      await forgetJob(job.id).catch(() => undefined);
      saved.delete(job.id);
    } catch (err) {
      // The app went away while this was going up: not a failure, an
      // interruption. Straight back in line if the app is already back --
      // the failure can arrive after the return that would have resumed it --
      // and held for that return otherwise.
      if (awayAt >= startedAt) {
        if (document.visibilityState === 'visible') pending.push(job);
        else stalled.push(job);
        continue;
      }
      if (refusedForGood(err)) {
        await forgetLeg(job.id).catch(() => undefined);
        await forgetJob(job.id).catch(() => undefined);
        saved.delete(job.id);
        const why = err instanceof Error ? `: ${err.message.replace(/\.$/, '')}` : '';
        publish({ error: `${label(job)} could not be sent${why}.` });
        continue;
      }
      // Left in IndexedDB on purpose: the next launch retries it rather than
      // dropping someone's photo because the signal died mid-upload. One that
      // never reached IndexedDB is held in memory for the same retry.
      if (!(await (saved.get(job.id) ?? true))) stalled.push(job);
      publish({
        error:
          err instanceof Error
            ? `${label(job)} did not finish: ${err.message}. It will retry when you come back.`
            : `${label(job)} did not finish. It will retry when you come back.`,
      });
    } finally {
      inFlight.delete(job.id);
    }
  }

  publish({ active: null, queued: 0 });
  running = false;
}

/** True while bytes are still going up, so the app can warn before a close. */
export function hasPendingUploads(): boolean {
  return running || pending.length > 0;
}

/**
 * Backgrounding the app — going to the home screen, locking the phone,
 * switching apps — freezes our JavaScript and usually kills the in-flight
 * request, but the page often stays loaded. So returning to it does not
 * re-run main.tsx, and a resume tied only to startup would never fire. This
 * hooks visibility instead, which covers both coming back to a live page and
 * a cold launch after the app was swiped away.
 */
export function watchForResume(): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void resumeUploads();
  });
}
